import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MessageCircleIcon } from 'lucide-react';
import { AccountAfterCheckout } from '../account/AccountAfterCheckout';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { useCatalog } from '../../contexts/CatalogContext';
import { isPhysical } from '../../core/rules/stock';
import { ease } from '../../core/format/motion';
import { shopLabels } from '../../data/shopLabels';
import { euroCents } from './checkoutRules';
import { OrderSourceAsk } from './OrderSourceAsk';
import type { PlacedOrder } from './useCheckout';
type Props = {
  placed: PlacedOrder;
  headingRef: RefObject<HTMLHeadingElement>;
  /** Uit `?t=` van de terugkeerlink (voor "Hoe kwam je bij ons?"); leeg = de sessie van de order. */
  token?: string;
};
const quietLink = 'inline-flex items-center gap-2 py-2 text-[14px] text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline';

/**
 * De bedankpagina als één rustige kolom, van boven naar beneden gelezen:
 * 1. de bevestiging;
 * 2. direct daaronder de éne volgende stap (je bestelling volgen, of een account maken),
 *    als het zwaarste blok op de pagina, zodat hij nooit meer wegvalt in een zijkolom;
 * 3. wat er daarna gebeurt, als korte tijdlijn;
 * 4. een vraag stellen of verder winkelen, als rustige links.
 */
export function OrderPlaced({
  placed,
  headingRef,
  token = ''
}: Props) {
  const reduce = useReducedMotion();
  const {
    productById
  } = useCatalog();
  const products = placed.accountOrder.lines.map(l => productById(l.productId));
  // Niets op de post (alleen cadeaubonnen en/of een kuur): geen bezorgdatum en geen inpakstap beloven.
  const nothingToShip = products.length > 0 && products.every(p => p !== undefined && !isPhysical(p));
  const hasKuur = products.some(p => p?.kind === 'kuur');
  const hasGiftCard = products.some(p => p?.kind === 'giftcard');
  const delivery = !nothingToShip ? `${policyCopy.delivery} (op werkdagen)` : hasKuur && hasGiftCard ? 'Cadeaubon per mail, kuur in Mijn omgeving' : hasKuur ? 'Je kuur staat in Mijn omgeving' : 'Per mail, direct na betaling';
  const secondStep = !nothingToShip ? {
    when: 'Bij het inpakken',
    text: `${founder.firstName} pakt je bestelling zelf in, met een handgeschreven gebruiksadvies.`
  } : hasKuur ? {
    when: 'Daarna',
    text: 'Plan je eerste sessie wanneer het je uitkomt. Hij is al betaald; je ziet in Mijn omgeving hoeveel sessies je nog hebt.'
  } : {
    when: 'Daarna',
    text: 'De cadeaubon komt in een aparte mail, klaar om door te sturen of te printen.'
  };
  const steps = [{
    when: 'Vandaag',
    text: `Je bevestiging gaat naar ${placed.email}. Niet gezien? Kijk even bij spam.`
  }, secondStep, {
    when: policyCopy.followUp,
    text: placed.remind ? 'Een appje om te horen hoe je huid reageert. Later volgt een seintje voordat je flesje op is.' : 'Een appje om te horen hoe je huid reageert. Echt, geen automatische mail.'
  }];
  return <section className="bg-sand" data-id="element-7267" data-mp="16g73q1dwhurh">
      <div className="mx-auto max-w-3xl px-6 pb-24 pt-12 lg:px-10 lg:pb-28 lg:pt-20" data-id="element-7268" data-mp="sjbizboaeevl">
        <motion.div initial={reduce ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-7269" data-mp="xhmqhfr40dan">
          {/* Het ene moment dat een klein, stevig accent verdient: de bestelling is binnen. */}
          <motion.span initial={reduce ? false : {
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 420,
          damping: 24,
          delay: 0.08
        }} className="flex h-12 w-12 items-center justify-center rounded-full bg-ink" aria-hidden="true" data-id="element-7270" data-mp="nkgxg1yxqc7s">
            <CheckIcon className="h-5 w-5 text-shell" strokeWidth={1.75} data-id="element-7271" data-mp="4t7l7kxs59ci" />
          </motion.span>
          <h1 ref={headingRef} tabIndex={-1} className="mt-7 font-display text-[2.6rem] font-light leading-[1.05] text-ink sm:text-6xl" data-id="element-7272" data-mp="176ly899n7hph">
            Dank je wel, {placed.firstName}.
          </h1>
          <p className="mt-5 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-7273" data-mp="yebp6vahhi9t">
            Je bestelling is binnen en ik ga ermee aan de slag.
          </p>
          {/* Drie feiten om terug te lezen, in plaats van één lange zin. */}
          <dl className="mt-7 grid max-w-xl gap-x-6 gap-y-3 border-t border-line pt-5 text-[15px] sm:grid-cols-[8.5rem_1fr]" data-id="element-7274" data-mp="oh70anp978pb">
            <dt className="text-muted" data-id="element-7275" data-mp="jfq1cxqbxwbd">Bestelling</dt>
            <dd className={placed.orderNumber ? 'font-medium tabular-nums text-ink' : 'text-muted'} data-id="element-7276" data-mp="fexsi5lddtfm">{placed.orderNumber ?? shopLabels.orderNumberLater}</dd>
            <dt className="text-muted" data-id="element-7277" data-mp="aifqvelc6b7i">Betaald</dt>
            <dd className="tabular-nums text-ink" data-id="element-7278" data-mp="29tqnc2efig6i">{euroCents(placed.total)}</dd>
            <dt className="text-muted" data-id="element-7279" data-mp="24ungccew1zvw">{nothingToShip ? 'Ontvangen' : 'Bezorging'}</dt>
            <dd className="text-ink" data-id="element-7280" data-mp="2cdwq46rb40lh">{delivery}</dd>
            {placed.gift && <>
                <dt className="text-muted" data-id="element-7281" data-mp="2hczudozgq2se">Cadeau erbij</dt>
                <dd className="text-ink" data-id="element-7282" data-mp="2hbexhspoe7rd">{placed.gift.split(' + ').join(', ')}</dd>
              </>}
          </dl>
        </motion.div>

        {/* De volgende stap staat direct onder de bevestiging: op mobiel én desktop het eerste wat je daarna ziet. */}
        <div className="mt-10" data-id="element-7283" data-mp="1tfu8id1395xp">
          <AccountAfterCheckout email={placed.email} firstName={placed.firstName} orderId={placed.orderId} order={placed.accountOrder} subscribeIds={placed.subscribeIds} intervalWeeks={placed.intervalWeeks} data-id="element-7284" data-mp="1tgvi0d2ghkdb" />
        </div>

        <div className="mt-16" data-id="element-7285" data-mp="27g4yzeyhkiic">
          <h2 className="font-display text-2xl font-light text-ink" data-id="element-7286" data-mp="3esnif6g0i4gd">Zo gaat het verder</h2>
          {/* Volgorde telt hier: dit gebeurt na elkaar. Op desktop naast elkaar, op mobiel onder elkaar. */}
          <ol className="relative mt-6 space-y-7 border-l border-line pl-7 sm:grid sm:grid-cols-3 sm:gap-8 sm:space-y-0 sm:border-l-0 sm:border-t sm:pl-0 sm:pt-6" data-id="element-7287" data-mp="1joyvshn8cr4g">
            {steps.map((s, i) => <li key={s.when} className="relative" data-id="element-7288" data-mp="1spna7dgjcthv" data-mpi={i}>
                <span aria-hidden="true" className={`absolute -left-[33px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-sand sm:-top-[31px] sm:left-0 ${i === 0 ? 'bg-clay' : 'bg-line'}`} data-id="element-7289" data-mp="1sp44qw82nxkq" />
                <p className="text-[14px] text-clay" data-id="element-7290" data-mp="vzwgvhjzdcdn">{s.when}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-7291" data-mp="w1h9h1mqil8v">{s.text}</p>
              </li>)}
          </ol>
        </div>

        {/* Ná betalen en onder alles wat ertoe doet: nooit een drempel, één tik. */}
        <OrderSourceAsk orderId={placed.orderId} token={token} ask={placed.askSource} data-id="element-7292" data-mp="hagt8afqxn26" />

        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 border-t border-line pt-6" data-id="element-7293" data-mp="cbne6dkt9nzw">
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={quietLink} data-id="element-7294" data-mp="2nz0pxltm761m">
            <MessageCircleIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7295" data-mp="2nx60pvbb8rrq" />
            Een vraag? App {founder.firstName}
            <span className="sr-only" data-id="element-7296" data-mp="2ezdk8nture2r">{shopLabels.viaWhatsApp}</span>
          </a>
          <Link to="/webshop" className={quietLink} data-id="element-7297" data-mp="2jwvtygfp1xbt">
            Verder winkelen
          </Link>
        </div>
      </div>
    </section>;
}